import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api";
import type {
  IndigoPreferences,
  Period,
  Smoothing,
  TimelineResponse,
} from "../../types/indigo";

/**
 * INDIGO Workstation · data-fetching hooks
 *
 * Each hook wraps a single backend endpoint and exposes the matching
 * TanStack Query primitive. Cache keys are namespaced under `workstation`
 * so invalidation stays surgical (e.g. PATCH /preferences only flushes
 * preferences, not the heavy /timeline query).
 *
 * Backend contract: api/routers/analytics_workstation.py (Phase B).
 */

const BASE = "/athlete/workstation";

export type HeatmapDay = {
  date: string;
  load: number;
  planned: number;
  sessions: number;
};

export type HeatmapResponse = {
  days: HeatmapDay[];
  weeks: number;
  peak_load: number;
};

export type SensitivityPoint = {
  date: string;
  ctl: number;
  atl: number;
  tsb: number;
};

export type SensitivityResponse = {
  tau_ctl: number;
  tau_atl: number;
  series: SensitivityPoint[];
};

export function useWorkstationSensitivity(params: {
  tau_ctl: number;
  tau_atl: number;
  weeks?: number;
  enabled?: boolean;
}) {
  const { tau_ctl, tau_atl, weeks = 12, enabled = true } = params;
  return useQuery({
    queryKey: ["workstation", "sensitivity", tau_ctl, tau_atl, weeks],
    queryFn: async (): Promise<SensitivityResponse> => {
      const qs = new URLSearchParams({
        tau_ctl: String(tau_ctl),
        tau_atl: String(tau_atl),
        weeks: String(weeks),
      });
      const { data } = await api.get<SensitivityResponse>(
        `${BASE}/sensitivity?${qs.toString()}`,
      );
      return data;
    },
    enabled,
    staleTime: 30 * 1000, // 30s — slider drag triggers fresh fetches
  });
}

export type PeriodizationPhase = {
  name: string;
  start_date: string;
  end_date: string;
  volume_pct: number;
  intensity_pct: number;
  // Phase 5 (design-recompose) — illustrative CTL guidance, not a forecast.
  ctl_target: number | null;
};

export type PeriodizationResponse = {
  phases: PeriodizationPhase[];
};

export function useWorkstationPeriodization() {
  return useQuery({
    queryKey: ["workstation", "periodization"],
    queryFn: async (): Promise<PeriodizationResponse> => {
      const { data } = await api.get<PeriodizationResponse>(
        `${BASE}/periodization`,
      );
      return data;
    },
    staleTime: 60 * 60 * 1000, // 1h — plan phases change rarely
  });
}

export function useWorkstationHeatmap(weeks = 12) {
  return useQuery({
    queryKey: ["workstation", "heatmap", weeks],
    queryFn: async (): Promise<HeatmapResponse> => {
      const { data } = await api.get<HeatmapResponse>(
        `${BASE}/heatmap?weeks=${weeks}`,
      );
      return data;
    },
    staleTime: 1000 * 60 * 5,
  });
}

export function useWorkstationTimeline(
  params: { period?: Period; smoothing?: Smoothing; metrics?: string[] } = {},
) {
  const { period = "last-12w", smoothing = "7d", metrics } = params;
  return useQuery({
    queryKey: ["workstation", "timeline", period, smoothing, metrics?.join(",") ?? "default"],
    queryFn: async (): Promise<TimelineResponse> => {
      const qs = new URLSearchParams({ period, smoothing });
      if (metrics && metrics.length) qs.set("metrics", metrics.join(","));
      const { data } = await api.get<TimelineResponse>(`${BASE}/timeline?${qs.toString()}`);
      return data;
    },
    staleTime: 1000 * 60 * 5, // 5 min — fitness data changes daily, not minute-by-minute
  });
}

// P2.1 W4 — HRV vs CTL lag-correlation hook.
export interface HrvCtlLagItem {
  lag_days: number;
  r: number | null;
}

export interface HrvCtlLagResponse {
  best_lag_days: number;
  r: number;
  significant: boolean;
  samples_at_best_lag: number;
  total_paired_days: number;
  per_lag: HrvCtlLagItem[];
  interpretation_key: string;
}

export function useHrvCtlLag(opts?: { days?: number; maxLag?: number }) {
  const days = opts?.days ?? 120;
  const maxLag = opts?.maxLag ?? 14;
  return useQuery<HrvCtlLagResponse | null>({
    queryKey: ["athlete", "hrv-ctl-lag", days, maxLag],
    queryFn: async () => {
      const { data } = await api.get<HrvCtlLagResponse | null>(
        `/athlete/analytics/hrv-ctl-lag?days=${days}&max_lag=${maxLag}`,
      );
      return data;
    },
    staleTime: 1000 * 60 * 30, // daily metric — 30 min cache plenty
  });
}


export function useWorkstationPreferences() {
  return useQuery({
    queryKey: ["workstation", "preferences"],
    queryFn: async (): Promise<IndigoPreferences> => {
      const { data } = await api.get<IndigoPreferences>(`${BASE}/preferences`);
      return data;
    },
    staleTime: Infinity, // preferences change only on explicit user action
  });
}

export function useUpdateWorkstationPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<IndigoPreferences>): Promise<IndigoPreferences> => {
      const { data } = await api.patch<IndigoPreferences>(`${BASE}/preferences`, patch);
      return data;
    },
    onSuccess: (data) => {
      qc.setQueryData(["workstation", "preferences"], data);
      // companion_mode flips the readout layer — refetch timeline + lens
      // so the next render reflects the new gating.
      qc.invalidateQueries({ queryKey: ["workstation", "timeline"] });
      qc.invalidateQueries({ queryKey: ["workstation", "lens"] });
    },
  });
}
